import { MS_PER_DAY } from './zone';

// Días calendario sin zona: texto `AAAA-MM-DD` (filtros en la URL, `<input type=date>`)
// y fechas puras que manda el API (columnas `@db.Date`: `AAAA-MM-DD` o medianoche UTC).
// Mismas reglas que el backend: fecha real y años 2000 a 2999.

/** Día calendario `AAAA-MM-DD`. */
export type Day = string;

const MIN_YEAR = 2000;
const MAX_YEAR = 2999;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
// Fecha pura del API: solo el día o la medianoche UTC exacta. Un instante con hora no
// es una fecha pura; tratarlo como tal correría el día.
const DATE_ONLY_RE = /^(\d{4}-\d{2}-\d{2})(?:T00:00:00(?:\.000)?Z)?$/;

const pad2 = (n: number): string => String(n).padStart(2, '0');

interface Ymd {
  y: number;
  m: number;
  d: number;
}

function partsOf(day: string): Ymd | null {
  const m = DAY_RE.exec(day);
  if (!m) {
    return null;
  }
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  return dayFromParts(y, mo, d) === null ? null : { y, m: mo, d };
}

function utcMs(day: Day): number {
  const p = partsOf(day);
  return p === null ? Number.NaN : Date.UTC(p.y, p.m - 1, p.d);
}

function fromUtcMs(ms: number): Day {
  const t = new Date(ms);
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

/** Día real de (año, mes, día) sin límite de años; null si no existe (30 de febrero). */
export function dayFromParts(y: number, m: number, d: number): Day | null {
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d
    ? `${String(y).padStart(4, '0')}-${pad2(m)}-${pad2(d)}`
    : null;
}

/** El día si es real y está entre 2000 y 2999; si no, null. */
export function parseDay(v: string | null): Day | null {
  const p = v === null ? null : partsOf(v);
  return p !== null && p.y >= MIN_YEAR && p.y <= MAX_YEAR ? v : null;
}

/**
 * Día de una fecha pura: texto del API (`AAAA-MM-DD` o su medianoche UTC) o un `Date`
 * cuyo día está en sus componentes UTC (fecha pura de Prisma o celda de Excel, que
 * `read-excel-file` entrega en UTC aunque traiga hora).
 */
export function dayOfDateOnly(value: string | Date | null): Day | null {
  if (value === null) {
    return null;
  }
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : fromUtcMs(ms);
  }
  const m = DATE_ONLY_RE.exec(value);
  return m?.[1] !== undefined && partsOf(m[1]) !== null ? m[1] : null;
}

/** Suma `n` días (entero) a un día. */
export function addDays(day: Day, n: number): Day {
  return fromUtcMs(utcMs(day) + n * MS_PER_DAY);
}

/** Primer día del mes que queda `n` meses después del de `day`. */
export function firstDayOfMonthAfter(day: Day, n: number): Day {
  const p = partsOf(day);
  return p === null ? day : fromUtcMs(Date.UTC(p.y, p.m - 1 + n, 1));
}

/** Días de `from` a `to` (negativo si `to` es anterior). */
export function daysBetweenDays(from: Day, to: Day): number {
  return Math.round((utcMs(to) - utcMs(from)) / MS_PER_DAY);
}

/** Cantidad de períodos (día, mes o año) que toca `from`..`to`, inclusivo. */
export function periodSpan(g: 'day' | 'month' | 'year', from: Day, to: Day): number {
  const a = partsOf(from);
  const b = partsOf(to);
  if (a === null || b === null) {
    return Number.NaN;
  }
  if (g === 'year') {
    return b.y - a.y + 1;
  }
  if (g === 'month') {
    return (b.y - a.y) * 12 + (b.m - a.m) + 1;
  }
  return daysBetweenDays(from, to) + 1;
}

/** Días entre dos fechas contando ambas. */
export function inclusiveDays(from: Day, to: Day): number {
  return daysBetweenDays(from, to) + 1;
}
